import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { animated, useSpring } from '@react-spring/web'
import { parsePhoneNumber } from 'libphonenumber-js'
import { Form, Link, data, redirect, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { AuthFormWrapper } from '#app/components/auth/AuthFormWrapper.js'
import { AuthHeader } from '#app/components/auth/AuthHeader.js'
import { AuthLayout } from '#app/components/auth/AuthLayout.js'
import { AuthProviders } from '#app/components/auth/AuthProviders.js'
import { OtpEmail } from '#app/components/emails/OtpEmail'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import {
	ErrorList,
	FloatingLabelField,
	FloatingPhoneInputField,
} from '#app/components/forms'
import { AnimatedContainer } from '#app/components/ui/animated-container'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { StaggerChildren } from '#app/components/ui/stagger-children'
import { smoothSpring } from '#app/utils/animations'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { requireAnonymous } from '#app/utils/auth.server'
import { capitalizeWords } from '#app/utils/capitalize'
import { db } from '#app/utils/db.server'
import { sendEmail } from '#app/utils/email.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { getEmailBaseUrl, useIsPending } from '#app/utils/misc'
import { redirectWithToast } from '#app/utils/toast.server'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import { verifySessionStorage } from '#app/utils/verification.server'
import { teamInvitations } from '#db/schema/team-invitations'
import type { Route } from './+types/landlord.signup'
import { prepareVerification } from './verify.server'

type SEOHandle = { getSitemapEntries: () => null }

export const handle = {
	getSitemapEntries: () => null,
} satisfies SEOHandle

const PhoneSchema = z
	.string({ required_error: 'Phone number is required' })
	.min(1, { message: 'Phone number is required' })
	.refine(
		(phone) => {
			if (!phone || phone.trim() === '') {
				return false
			}
			try {
				const parsed = parsePhoneNumber(phone)
				return parsed?.isValid() ?? false
			} catch {
				try {
					const parsed = parsePhoneNumber(phone, 'CA')
					return parsed?.isValid() ?? false
				} catch {
					return false
				}
			}
		},
		{
			message: 'Please enter a valid phone number',
		},
	)

const FirstNameSchema = z
	.string({ required_error: 'First name is required' })
	.min(1, { message: 'First name is required' })
	.max(50, { message: 'First name is too long' })
	// Title-case so the stored name has a capital first letter regardless of typing.
	.transform((value) => capitalizeWords(value))

const LastNameSchema = z
	.string({ required_error: 'Last name is required' })
	.min(1, { message: 'Last name is required' })
	.max(50, { message: 'Last name is too long' })
	.transform((value) => capitalizeWords(value))

const CompanyNameSchema = z
	.string({ required_error: 'Company name is required' })
	.min(2, { message: 'Company name must be at least 2 characters' })
	.max(100, { message: 'Company name is too long' })

const SignupSchema = z
	.object({
		firstname: FirstNameSchema,
		lastname: LastNameSchema,
		companyName: CompanyNameSchema,
		email: EmailSchema,
		phone: PhoneSchema,
		password: PasswordSchema,
		confirmPassword: PasswordSchema,
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('landlord')
	const url = new URL(request.url)
	const inviteTokenParam = url.searchParams.get('invite_token')

	// Resolve the canonical invite email server-side from the token.
	// Never trust an `email` query param: the token is the only thing tying
	// this signup to a specific invitation and its tenant/role.
	let inviteToken: string | null = null
	let inviteEmail: string | null = null
	if (inviteTokenParam) {
		const invitation = await db.query.teamInvitations.findFirst({
			where: (t, { eq }) => eq(t.token, inviteTokenParam),
			columns: { email: true, status: true, expiresAt: true },
		})

		if (!invitation) {
			throw await redirectWithToast('/landlord/login', {
				title: 'Invalid Invitation',
				description:
					'This invitation link is invalid or has already been used.',
				type: 'error',
			})
		}
		if (invitation.status !== 'pending') {
			throw await redirectWithToast('/landlord/login', {
				title: 'Invitation Unavailable',
				description: `This invitation has been ${invitation.status}.`,
				type: 'error',
			})
		}
		if (invitation.expiresAt && new Date(invitation.expiresAt) < new Date()) {
			throw await redirectWithToast('/landlord/login', {
				title: 'Invitation Expired',
				description:
					'This invitation has expired. Please ask your admin to resend it.',
				type: 'error',
			})
		}

		inviteToken = inviteTokenParam
		inviteEmail = invitation.email
	}

	return { authConfig, inviteToken, inviteEmail }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)

	const inviteTokenFromForm = formData.get('inviteToken')?.toString() || null

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const submittedEmail = data.email.toLowerCase()

			// If signing up through a team invitation, the email is bound to the
			// invitation. The client renders the field read-only, but the form
			// can still be tampered with — re-validate here against the DB.
			if (inviteTokenFromForm) {
				const invitation = await db.query.teamInvitations.findFirst({
					where: (t, { eq }) => eq(t.token, inviteTokenFromForm),
					columns: { email: true, status: true, expiresAt: true },
				})

				if (
					!invitation ||
					invitation.status !== 'pending' ||
					(invitation.expiresAt && new Date(invitation.expiresAt) < new Date())
				) {
					ctx.addIssue({
						path: ['email'],
						code: z.ZodIssueCode.custom,
						message: 'This invitation is no longer valid.',
					})
					return
				}

				if (submittedEmail !== invitation.email.toLowerCase()) {
					ctx.addIssue({
						path: ['email'],
						code: z.ZodIssueCode.custom,
						message: 'This invitation was sent to a different email address.',
					})
					return
				}
			}

			const existingUser = await db.query.user.findFirst({
				columns: { id: true },
				where: (user, { eq }) => eq(user.email, submittedEmail),
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{
				status: 500,
			},
		)
	}
	const { email, firstname, lastname, companyName, phone, password } =
		submission.value
	const redirectToValue = formData.get('redirectTo')?.toString()
	const {
		redirectTo,
		otp,
		verifyUrl: _verifyUrl,
	} = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
		redirectTo: redirectToValue,
	})

	// Store password and user data in verification session
	const verifySession = await verifySessionStorage.getSession()
	verifySession.set('onboardingPassword', password)
	verifySession.set('onboardingFirstName', firstname)
	verifySession.set('onboardingLastName', lastname)
	verifySession.set('onboardingPhone', phone)
	verifySession.set('onboardingCompanyName', companyName)
	verifySession.set('onboardingUserType', 'landlord')

	// Store redirectTo if present (for post-onboarding redirect)
	if (redirectToValue) {
		verifySession.set('onboardingRedirectTo', redirectToValue)
	}

	// Store invitation token if present (for team invitation processing)
	if (inviteTokenFromForm) {
		verifySession.set('onboardingInviteToken', inviteTokenFromForm)
	}

	console.log('otp', otp)
	// Send email with OTP (optional - don't block if it fails)
	try {
		const response = await sendEmail({
			to: email,
			subject: 'Welcome to TrueTenant!',
			react: (
				<OtpEmail
					otp={otp}
					baseUrl={getEmailBaseUrl(request)}
					type="confirm-email"
				/>
			),
		})
		if (response.status === 'error') {
			// Log error but don't block signup
			console.error('Failed to send verification email:', response.error)
		}
	} catch (error) {
		// Log error but don't block signup
		console.error('Error sending verification email:', error)
	}

	// Data is stored in session - no need to add to URL for security
	// URL params removed for security - session data will be used instead

	return redirect(redirectTo.toString(), {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Sign Up | TrueTenant - Landlord' }]
}

export default function LandlordSignupRoute({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const authConfig = loaderData.authConfig
	const inviteToken = loaderData.inviteToken
	const inviteEmail = loaderData.inviteEmail

	// Build login link preserving redirectTo param
	const loginLink = redirectTo
		? `${authConfig.loginPath}?redirectTo=${encodeURIComponent(redirectTo)}`
		: authConfig.loginPath

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	// React Spring animation for error messages
	const hasErrors = Boolean(form.errors?.length)
	const errorSpring = useSpring({
		opacity: hasErrors ? 1 : 0,
		y: hasErrors ? 0 : -10,
		config: smoothSpring,
	})

	return (
		<AuthLayout
			backgroundImage={authConfig.backgroundImage}
			heroText={authConfig.heroText}
			userType="landlord"
		>
			<AuthHeader />
			<AuthFormWrapper
				title={authConfig.titles.signup}
				subtitle={authConfig.subtitles.signup}
				titleClassName="text-[#32302F] text-[20px] md:text-[35px] capitalize font-medium"
				subtitleClassName="text-[#747474] font-400 text-[18px] md:text-[20px]"
			>
				<AnimatedContainer>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						{redirectTo && (
							<input type="hidden" name="redirectTo" value={redirectTo} />
						)}
						{inviteToken && (
							<input type="hidden" name="inviteToken" value={inviteToken} />
						)}
						<StaggerChildren>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] mb-[20px]">
								<FloatingLabelField
									inputProps={{
										...getInputProps(fields.firstname, { type: 'text' }),
										autoFocus: true,
										autoComplete: 'given-name',
										placeholder: 'Enter Your First Name',
										// Title-case as the user types (caret preserved — length
										// is unchanged), matching the screening name fields.
										onChange: (e) => {
											const el = e.currentTarget
											const pos = el.selectionStart
											el.value = capitalizeWords(el.value)
											if (pos !== null) el.setSelectionRange(pos, pos)
										},
										className:
											'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
									}}
									labelProps={{
										children: 'First Name',
										htmlFor: fields.firstname.id,
										className: 'text-[16px] text-[#32302F]',
									}}
									errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
									errors={fields.firstname.errors}
								/>

								<FloatingLabelField
									inputProps={{
										...getInputProps(fields.lastname, { type: 'text' }),
										autoComplete: 'family-name',
										placeholder: 'Enter Your Last Name',
										onChange: (e) => {
											const el = e.currentTarget
											const pos = el.selectionStart
											el.value = capitalizeWords(el.value)
											if (pos !== null) el.setSelectionRange(pos, pos)
										},
										className:
											'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
									}}
									labelProps={{
										children: 'Last Name',
										htmlFor: fields.lastname.id,
										className: 'text-[16px] text-[#32302F]',
									}}
									errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
									errors={fields.lastname.errors}
								/>
							</div>

							<FloatingLabelField
								inputProps={{
									...getInputProps(fields.companyName, { type: 'text' }),
									autoComplete: 'organization',
									placeholder: 'Enter Your Company / Organization Name',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
								}}
								labelProps={{
									children: 'Company Name',
									htmlFor: fields.companyName.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
								className="mb-[20px]"
								errors={fields.companyName.errors}
							/>

							<FloatingLabelField
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									autoComplete: 'email',
									placeholder: 'Enter Your Email Address',
									defaultValue: inviteEmail || undefined,
									readOnly: Boolean(inviteEmail),
									'aria-readonly': inviteEmail ? true : undefined,
									title: inviteEmail
										? 'This email is locked to your invitation.'
										: undefined,
									className: `placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]${
										inviteEmail
											? ' bg-[#F5F5F4] cursor-not-allowed text-[#747474]'
											: ''
									}`,
								}}
								labelProps={{
									children: 'Email',
									htmlFor: fields.email.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0 md:pb-0 '}
								className="mb-[20px]"
								errors={fields.email.errors}
							/>
							{inviteEmail ? (
								<p className="-mt-[12px] mb-[20px] px-1 text-[12px] text-[#747474]">
									This invitation is locked to{' '}
									<span className="font-medium text-[#32302F]">
										{inviteEmail}
									</span>
									. To accept it, sign up with this email.
								</p>
							) : null}
							<FloatingPhoneInputField
								inputProps={{
									...getInputProps(fields.phone, { type: 'text' }),
									defaultCountry: 'CA',
									placeholder: '',
									className: '',
								}}
								labelProps={{
									children: 'Phone Number',
									className: 'text-[#32302F]',
								}}
								errors={fields.phone.errors}
								className="mb-[20px]"
								errorClass="md:px-0 md:pt-0 md:pb-0"
							/>

							<FloatingLabelField
								passwordHide
								inputProps={{
									...getInputProps(fields.password, { type: 'password' }),
									autoComplete: 'new-password',
									placeholder: 'Enter Password',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] border border-[#CCCCCC] rounded-[30px] text-[#484848]',
								}}
								labelProps={{
									children: 'Password',
									htmlFor: fields.password.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
								className="mb-[20px]"
								errors={fields.password.errors}
							/>
							<FloatingLabelField
								passwordHide
								inputProps={{
									...getInputProps(fields.confirmPassword, {
										type: 'password',
									}),
									autoComplete: 'new-password',
									placeholder: 'Confirm Password',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] border border-[#CCCCCC] rounded-[30px] text-[#484848]',
								}}
								labelProps={{
									children: 'Confirm Password',
									htmlFor: fields.confirmPassword.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0 '}
								errors={fields.confirmPassword.errors}
							/>
							<animated.div style={errorSpring} className="mt-6 mb-2">
								<ErrorList errors={form.errors} id={form.errorId} />
							</animated.div>
							<AnimatedStatusButton
								className="w-full py-[23px] h-[55px] mt-[32px] mb-[20px] bg-[#32302F] text-[20px] text-[#FFFFFF] capitalize rounded-[30px] hover:bg-[#1a1918] hover:text-[#FFFFFF] active:bg-[#0f0f0e] transition-colors duration-200 border-0"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
								variant={'outline'}
							>
								Create Account
							</AnimatedStatusButton>
							<p className="text-[#32302F] font-normal text-[16px] text-center mt-8 mb-6">
								Already have an account?
								<span
									className="text-[#32302F] text-[16px] font-normal"
									aria-hidden
								>
									{' '}
								</span>{' '}
								<Link
									to={loginLink}
									className="text-black text-md font-500 text-[16px] underline font-medium"
								>
									Log in
								</Link>
							</p>
						</StaggerChildren>
					</Form>
					<AnimatedContainer delay={0.3}>
						<AuthProviders
							type="Signup"
							redirectTo={redirectTo}
							userType="landlord"
						/>
					</AnimatedContainer>
				</AnimatedContainer>
			</AuthFormWrapper>
		</AuthLayout>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
